/**
 * Tide reference locations — the named marinas and yacht havens on the RYA
 * training charts. They carry NO tide feed of their own: eNav publishes HW/LW for
 * the standard and secondary PORTS, not for every marina. Rather than fabricate a
 * curve, we treat each marina as a location that reads its tides off the NEAREST
 * tide station, which is the standard RYA practice for a berth without its own
 * predictions. That keeps the picker honest: real charted positions, real tides
 * from a real station, nothing invented.
 *
 * Positions are the centroids of the charted harbour facilities (HRBFAC) in the
 * training chart cells (public/charts/GB3RYA30 and the Chart 4 insets).
 */
import {
  nearestHeightPort,
  getHeightPort,
  DEFAULT_HEIGHT_PORT_ID,
  type HeightPort,
} from './heights';

export interface TidePlace {
  /** Stable slug id, e.g. 'endalmarina'. */
  id: string;
  /** Charted name shown in the picker. */
  name: string;
  /** Charted position [lon, lat]. */
  position: [number, number];
}

/** Named marinas / yacht havens on the training charts, alphabetical by name. */
export const TIDE_PLACES: TidePlace[] = [
  { id: 'blackmillmarina', name: 'Blackmill Marina', position: [-5.80423, 45.62162] },
  { id: 'chidhammarina', name: 'Chidham Marina', position: [-5.68903, 46.4388] },
  { id: 'colvillemarina', name: 'Colville Marina', position: [-6.14451, 45.94964] },
  { id: 'coombesburymarina', name: 'Coombesbury Marina', position: [-6.01602, 46.41328] },
  { id: 'dolphinmarina', name: 'Dolphin Marina', position: [-6.01495, 46.40505] },
  { id: 'emsbournemarina', name: 'Emsbourne Marina', position: [-5.7756, 46.46183] },
  { id: 'endalmarina', name: 'Endal Marina', position: [-5.78078, 46.23277] },
  { id: 'hamiltonmarina', name: 'Hamilton Marina', position: [-6.15357, 45.62278] },
  { id: 'namleymarina', name: 'Namley Marina', position: [-5.7858, 46.42813] },
  { id: 'northnamleyyachthaven', name: 'North Namley Yacht Haven', position: [-5.80411, 46.45603] },
  { id: 'novembermarina', name: 'November Marina', position: [-6.16063, 45.64129] },
  { id: 'nutworthmarina', name: 'Nutworth Marina', position: [-5.76356, 46.4583] },
  { id: 'quarrymarina', name: 'Quarry Marina', position: [-5.64268, 46.20186] },
  { id: 'rawmarshmarina', name: 'Rawmarsh Marina', position: [-5.60338, 46.33871] },
  { id: 'rozellecovemarina', name: 'Rozelle Cove Marina', position: [-5.90525, 46.29264] },
  { id: 'saintkildamarina', name: 'Saint Kilda Marina', position: [-6.06025, 45.81597] },
  { id: 'seahorsemarina', name: 'Seahorse Marina', position: [-5.9926, 46.43441] },
  { id: 'setterhallmarina', name: 'Setter Hall Marina', position: [-6.00628, 46.18363] },
  { id: 'suzybaymarina', name: 'Suzy Bay Marina', position: [-6.01725, 46.24944] },
  { id: 'victoriamarina', name: 'Victoria Marina', position: [-6.20959, 46.45056] },
  { id: 'whalebaymarina', name: 'Whale Bay Marina', position: [-5.60417, 46.35237] },
];

const BY_ID = new Map<string, TidePlace>(TIDE_PLACES.map((p) => [p.id, p]));

/** A reference location by id, or undefined for a port id (or an unknown id). */
export function getTidePlace(id: string): TidePlace | undefined {
  return BY_ID.get(id);
}

export interface HeightSelection {
  /** The tide station whose data is shown. */
  port: HeightPort;
  /** Set when the user picked a marina; the port is then its nearest station. */
  place?: TidePlace;
  /** Distance from the marina to that station, nautical miles. */
  distNm?: number;
}

/**
 * Resolve a picker selection (a port id OR a marina id) to the tide station that
 * supplies the data. A marina resolves to its nearest station; a port id resolves
 * to itself. Unknown ids fall back to the default port.
 */
export function resolveHeightSelection(id: string): HeightSelection {
  const place = getTidePlace(id);
  if (!place) return { port: getHeightPort(id) };
  const n = nearestHeightPort(place.position);
  if (n) return { port: n.port, place, distNm: n.distNm };
  return { port: getHeightPort(DEFAULT_HEIGHT_PORT_ID), place };
}
